/**
 * Notification Center - Wave 1 (2026-05-27).
 *
 * Full inbox view of /v1/notifications/* with filters by source,
 * severity, type. Operator can mark-read, acknowledge, dismiss,
 * snooze, or jump to the linked artefact.
 */
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Bell,
  CheckCheck,
  EyeOff,
  ClockArrowDown,
  ExternalLink,
  Filter,
  Inbox,
  RefreshCw,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { KpiCard } from '@/components/viz/KpiCard';
import { LiveBadge } from '@/components/ui/live-badge';
import { EmptyStateV2 } from '@/components/ui/empty-state-v2';
import { ActionRow, type RowAction } from '@/components/ui/action-row';
import { useNotificationStream } from '@/lib/use-notification-stream';
import { useMotionPreset } from '@/lib/motion-level';
import {
  acknowledgeNotification,
  dismissNotification,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  snoozeNotification,
  type NotificationItem,
  type NotificationSeverity,
  type NotificationSource,
} from '@/lib/api';
import { cn } from '@/lib/cn';

const SEVERITY_DOT: Record<NotificationSeverity, string> = {
  critical: 'bg-destructive',
  high: 'bg-warning',
  medium: 'bg-info',
  info: 'bg-muted-foreground',
};

const SEVERITY_LABEL: Record<NotificationSeverity, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  info: 'Info',
};

const SOURCES: { value: NotificationSource; label: string }[] = [
  { value: 'discovery', label: 'Discovery' },
  { value: 'activity', label: 'Activity' },
  { value: 'detection', label: 'Detection' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'foundational', label: 'Platform' },
  { value: 'platform', label: 'Platform' },
  { value: 'system', label: 'System' },
];

function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diffMs / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export function NotificationsCenter(): JSX.Element {
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterSeverity, setFilterSeverity] = useState<NotificationSeverity | null>(null);
  const [filterSource, setFilterSource] = useState<NotificationSource | null>(null);
  const [showRead, setShowRead] = useState(true);
  const { rise, transition } = useMotionPreset();

  async function refresh(): Promise<void> {
    setLoading(true);
    try {
      const res = await listNotifications({
        limit: 50,
        includeRead: showRead,
        ...(filterSeverity ? { severity: filterSeverity } : {}),
        ...(filterSource ? { source: filterSource } : {}),
      });
      setItems(res.notifications);
      setUnreadCount(res.unreadCount);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSeverity, filterSource, showRead]);

  // Live stream - prepend new items.
  const { status: streamStatus } = useNotificationStream({
    onMessage: (n) => {
      setItems((prev) => [n, ...prev.filter((p) => p.id !== n.id)]);
      if (n.unread) setUnreadCount((c) => c + 1);
    },
  });

  function actionsFor(n: NotificationItem): RowAction[] {
    return [
      {
        id: 'read',
        label: n.unread ? 'Mark read' : 'Read',
        icon: CheckCheck,
        disabled: !n.unread,
        kind: 'default',
        onClick: async () => {
          await markNotificationRead(n.id);
          setItems((prev) => prev.map((p) => (p.id === n.id ? { ...p, unread: false } : p)));
          setUnreadCount((c) => Math.max(0, c - 1));
        },
      },
      {
        id: 'ack',
        label: n.unacknowledged ? 'Acknowledge' : 'Acknowledged',
        icon: CheckCheck,
        disabled: !n.unacknowledged,
        kind: 'success',
        onClick: async () => {
          await acknowledgeNotification(n.id);
          setItems((prev) =>
            prev.map((p) =>
              p.id === n.id ? { ...p, unread: false, unacknowledged: false } : p,
            ),
          );
        },
      },
      {
        id: 'snooze',
        label: 'Snooze 1h',
        icon: ClockArrowDown,
        kind: 'warning',
        onClick: async () => {
          await snoozeNotification(n.id, new Date(Date.now() + 3600_000).toISOString());
          setItems((prev) => prev.filter((p) => p.id !== n.id));
        },
      },
      {
        id: 'dismiss',
        label: 'Dismiss',
        icon: EyeOff,
        kind: 'ghost',
        onClick: async () => {
          await dismissNotification(n.id);
          setItems((prev) => prev.filter((p) => p.id !== n.id));
        },
      },
      ...(n.url
        ? [
            {
              id: 'open',
              label: 'Open',
              icon: ExternalLink,
              kind: 'default' as const,
              onClick: () => {
                window.location.href = n.url;
              },
            },
          ]
        : []),
    ];
  }

  const critical = items.filter((i) => i.severity === 'critical').length;
  const high = items.filter((i) => i.severity === 'high').length;

  return (
    <PageContainer>
      <PageHeader
        title="Notifications"
        subtitle="All alerts, decisions, breaches, and platform events for this tenant. Real-time stream, persistent inbox, regulator-grade audit trail."
        badges={[
          { label: 'Real-time SSE', variant: 'glass' },
          { label: 'Hash-chained audit', variant: 'info' },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <LiveBadge status={streamStatus === 'live' ? 'live' : streamStatus === 'connecting' ? 'connecting' : 'idle'} />
            {unreadCount > 0 && (
              <Button
                size="sm"
                variant="glass"
                onClick={async () => {
                  await markAllNotificationsRead();
                  setItems((prev) => prev.map((p) => ({ ...p, unread: false })));
                  setUnreadCount(0);
                }}
              >
                <CheckCheck className="h-3.5 w-3.5 mr-1.5" />
                Mark all read
              </Button>
            )}
            <Button size="sm" variant="glass" onClick={() => void refresh()} disabled={loading}>
              <RefreshCw className={cn('h-3.5 w-3.5 mr-1.5', loading && 'animate-spin')} />
              Refresh
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Unread" value={unreadCount} icon={Bell} glow={unreadCount > 0 ? 'high' : 'low'} />
        <KpiCard label="Critical" value={critical} icon={Bell} glow={critical > 0 ? 'critical' : 'low'} hint="immediate action" />
        <KpiCard label="High" value={high} icon={Bell} glow={high > 0 ? 'medium' : 'low'} />
        <KpiCard label="Total in view" value={items.length} icon={Inbox} />
      </div>

      <GlassCard className="p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Filter className="h-3.5 w-3.5" /> Filters:
          </div>
          <div className="flex gap-1.5">
            <Button
              size="sm"
              variant={filterSeverity === null ? 'default' : 'glass'}
              onClick={() => setFilterSeverity(null)}
            >
              All severities
            </Button>
            {(['critical', 'high', 'medium', 'info'] as const).map((s) => (
              <Button
                key={s}
                size="sm"
                variant={filterSeverity === s ? 'default' : 'glass'}
                onClick={() => setFilterSeverity(s)}
              >
                <span className={cn('inline-block h-2 w-2 rounded-full mr-1.5', SEVERITY_DOT[s])} />
                {SEVERITY_LABEL[s]}
              </Button>
            ))}
          </div>
          <div className="flex gap-1.5">
            <Button
              size="sm"
              variant={filterSource === null ? 'default' : 'glass'}
              onClick={() => setFilterSource(null)}
            >
              All sources
            </Button>
            {SOURCES.slice(0, 4).map((s) => (
              <Button
                key={s.value}
                size="sm"
                variant={filterSource === s.value ? 'default' : 'glass'}
                onClick={() => setFilterSource(s.value)}
              >
                {s.label}
              </Button>
            ))}
          </div>
          <label className="ml-auto flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={showRead}
              onChange={(e) => setShowRead(e.target.checked)}
              className="h-3.5 w-3.5"
            />
            Show read
          </label>
        </div>
      </GlassCard>

      {items.length === 0 ? (
        <EmptyStateV2
          icon={Inbox}
          title="No notifications match these filters"
          description={
            loading
              ? 'Loading the inbox…'
              : 'When findings, decisions, breaches, or DSAR events fire - they land here in real time. Try clearing filters or check back later.'
          }
        />
      ) : (
        <div className="space-y-2">
          {/* Wave 8 (2026-05-27) - AnimatePresence with layout so
              dismiss / snooze rows fade + slide out instead of
              jumping. mode unset (parallel) so new SSE rows can
              prepend smoothly while old ones exit. */}
          <AnimatePresence initial={false}>
          {items.map((n, i) => (
            <motion.div
              key={n.id}
              layout
              initial={rise.hidden}
              animate={rise.visible}
              exit={{ opacity: 0, x: 24, transition: { duration: 0.2 } }}
              transition={{ ...transition, delay: Math.min(i * 0.02, 0.2) }}
            >
              <GlassCard
                className={cn(
                  'p-4 transition-colors',
                  n.unread && 'border-primary/40 bg-primary/[0.03]',
                )}
              >
                <div className="flex items-start gap-3">
                  <span
                    className={cn(
                      'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                      SEVERITY_DOT[n.severity],
                    )}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={cn('text-sm', n.unread && 'font-semibold')}>
                        {n.title}
                      </span>
                      <Badge variant="glass" className="text-[9px] uppercase">
                        {n.source}
                      </Badge>
                      <Badge
                        variant={
                          n.severity === 'critical'
                            ? 'critical'
                            : n.severity === 'high'
                              ? 'warning'
                              : 'glass'
                        }
                        className="text-[9px] uppercase"
                      >
                        {SEVERITY_LABEL[n.severity]}
                      </Badge>
                      <span className="text-[10px] text-muted-foreground">
                        {timeAgo(n.emittedAt)}
                      </span>
                    </div>
                    {n.body && (
                      <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                        {n.body}
                      </p>
                    )}
                    <div className="mt-2.5">
                      <ActionRow actions={actionsFor(n)} />
                    </div>
                  </div>
                </div>
              </GlassCard>
            </motion.div>
          ))}
          </AnimatePresence>
        </div>
      )}
    </PageContainer>
  );
}
